import React, { useEffect, useRef } from "react";
import TimerDisplay from "./TimerDisplay";
import PreCountdown from "./PreCountdown";
import axios from "axios";
import { useI18n } from "@/context/I18nContext";
import { serverNow } from "@/utils/serverClock";
import { countries } from "@/data/countries";
import { ss, offsetTransform, offsetTransformMirrored } from "@/utils/displayStyle";

import { API, BACKEND_URL } from '../../utils/apiBase';

const getImg = (url) => {
  if (!url) return '';
  if (url.startsWith('https://res.cloudinary.com/') || url.startsWith('http://res.cloudinary.com/')) return url;
  return `${BACKEND_URL}${url}`;
};

// Small flag badge that overlays the bottom-right of the athlete photo.
// Uses % of the parent so it auto-scales with the CSS-driven photo.
const FlagBadge = ({ countryName, theme, screen = "battle_run", element = "side_flag", mirrorX = false }) => {
  if (!countryName) return null;
  const match = countries.find(c => c.name.toLowerCase() === countryName.toLowerCase());
  if (!match) return null;
  const flagS = ss(theme, screen, element);
  const flagSizePercent = flagS.size * 30;
  const mx = (flagS.offset_x || 0) * (mirrorX ? -1 : 1);
  return (
    <div
      className="absolute rounded-full overflow-hidden"
      style={{
        width: `${flagSizePercent}%`,
        aspectRatio: "1 / 1",
        bottom: `${-4 + (flagS.offset_y || 0)}px`,
        right: `${-4 + mx}px`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
        border: "2px solid rgba(255,255,255,0.85)",
        zIndex: 10,
      }}
    >
      <img
        src={`/flags/${match.code}.svg`}
        alt={countryName}
        className="w-full h-full object-cover"
        onError={(e) => { e.target.style.display = "none"; }}
      />
    </div>
  );
};

const clamp = (v, min, max, def) => {
  const n = parseInt(v);
  if (isNaN(n)) return def;
  return Math.max(min, Math.min(max, n));
};

export const isTimerLayoutActive = (kind, timer) => {
  if (!timer) return false;
  if (kind === "battle") {
    return !!timer.first_athlete && ["pre_start", "running", "paused"].includes(timer.status);
  }
  if (kind === "circle") {
    return ["pre_countdown", "running", "paused"].includes(timer.status);
  }
  return false;
};

export const TimerActiveDisplay = ({ kind, timer, athlete1, athlete2, currentCircle, athletes, positionOrder, theme, soundEnabled = false }) => {
  const { t } = useI18n();
  const advancedRef = useRef(false);
  const circleId = currentCircle?.circle?.id;

  // Auto-advance for circles: any client (display/manager) calls /advance when local countdown hits 0.
  // The server is idempotent, so multiple clients calling is safe and improves resilience
  // (e.g. if the manager closed their tab, the display keeps the chain going).
  useEffect(() => {
    advancedRef.current = false;
  }, [timer?.segment_started_at, timer?.segment_index]);

  useEffect(() => {
    if (kind !== "circle" || !timer || timer.status !== "running" || !circleId) return;
    const checkAdvance = async () => {
      if (advancedRef.current) return;
      const duration = timer.duration_ms || 30000;
      let elapsed = timer.segment_paused_elapsed_ms || 0;
      if (timer.segment_started_at) {
        elapsed += serverNow() - new Date(timer.segment_started_at).getTime();
      }
      if (elapsed >= duration) {
        advancedRef.current = true;
        try {
          await axios.post(`${API}/circle/${circleId}/timer/advance`);
        } catch (_e) { /* ignore */ }
      }
    };
    const iv = setInterval(checkAdvance, 200);
    return () => clearInterval(iv);
  }, [kind, circleId, timer?.status, timer?.segment_started_at, timer?.segment_paused_elapsed_ms]);

  if (!timer) return null;

  const status = timer.status;
  const timerSizePct = clamp(theme?.display_timer_size, 50, 200, 100);
  const cornerSizePct = clamp(theme?.display_timer_corner_size, 50, 200, 100);
  const roundColor = theme?.display_timer_round_color || "#FFFFFF";
  const positionColor = theme?.display_timer_position_color || "#FFFFFF";
  const circleLabelColor = theme?.display_timer_circle_color || roundColor;
  const circleNumber = currentCircle?.circle?.circle_number;

  // Pre-countdown screen for circles (CIRCLE START — "Get ready! 3-2-1")
  if (kind === "circle" && status === "pre_countdown") {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10" data-testid="timer-active-pre-countdown">
        <PreCountdown timer={timer} playSounds={soundEnabled} screen="circle_start" onComplete={async () => {
          try {
            if (currentCircle?.circle?.id) {
              await axios.post(`${API}/circle/${currentCircle.circle.id}/timer/begin-segments`);
            }
          } catch (_e) { /* ignore */ }
        }} />
      </div>
    );
  }

  let activeAthleteId = null;
  let circleSeg = null;

  if (kind === "battle") {
    activeAthleteId = timer.current_athlete === "a1" ? athlete1?.id : timer.current_athlete === "a2" ? athlete2?.id : null;
  } else if (kind === "circle") {
    const seq = timer.sequence || [];
    const idx = timer.segment_index || 0;
    circleSeg = seq[idx];
    if (circleSeg) {
      const athleteIdsArr = currentCircle?.circle?.athlete_ids || [];
      activeAthleteId = athleteIdsArr[circleSeg.position - 1] || null;
    }
  }

  if (kind === "battle") {
    const roundS = ss(theme, "battle_run", "round_label", { color: theme?.display_timer_round_color });
    return (
      <div className="absolute inset-0 overflow-hidden z-10" data-testid="timer-active-battle">
        <div className="h-full w-full grid grid-cols-3 items-center px-[3vw]">
          <SideAthlete athlete={athlete1} active={activeAthleteId === athlete1?.id} side="left" theme={theme} sizePct={cornerSizePct} t={t} />
          <div className="flex flex-col items-center justify-center px-4">
            <div
              className="font-black mb-4 uppercase tracking-widest"
              style={{
                color: roundS.color,
                fontSize: `clamp(${2 * roundS.size}rem, ${4 * roundS.size}vw, ${5 * roundS.size}rem)`,
                textShadow: roundS.textShadow,
                transform: offsetTransform(roundS),
              }}
            >
              {t('timer.round')} {timer.round}
            </div>
            <TimerDisplay timer={timer} variant="huge" sizeMultiplier={timerSizePct} playSounds={soundEnabled} showProgress={false} screen="battle_run" />
          </div>
          <SideAthlete athlete={athlete2} active={activeAthleteId === athlete2?.id} side="right" theme={theme} sizePct={cornerSizePct} t={t} />
        </div>
      </div>
    );
  }

  if (kind === "circle") {
    const size = currentCircle?.circle?.size || 4;
    const athleteIds = currentCircle?.circle?.athlete_ids || [];
    const forfeitedIds = currentCircle?.circle?.forfeited_ids || [];
    const slotPositions = positionOrder === "1to4" ? [1, 2, 3, 4] : [4, 3, 2, 1];
    const corners = ["top-left", "top-right", "bottom-right", "bottom-left"];

    return (
      <div className="absolute inset-0 overflow-hidden z-10" data-testid="timer-active-circle">
        {slotPositions.map((pos, i) => {
          const aid = pos <= size ? athleteIds[pos - 1] : null;
          const athlete = aid ? athletes.find(a => a?.id === aid) : null;
          const isForfeit = aid ? forfeitedIds.includes(aid) : false;
          // Hide empty slots completely (no placeholder)
          if (!aid) return null;
          return (
            <CornerAthlete
              key={i}
              athlete={athlete}
              active={!isForfeit && aid === activeAthleteId}
              position={corners[i]}
              positionLabel={`P${pos}`}
              forfeited={isForfeit}
              theme={theme}
              sizePct={cornerSizePct}
              t={t}
            />
          );
        })}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          {circleNumber != null && (() => {
            const titleS = ss(theme, "circle_run", "circle_title", { color: circleLabelColor });
            return (
              <div
                className="font-black uppercase tracking-widest leading-none"
                style={{
                  color: titleS.color,
                  fontSize: `clamp(${1.5 * titleS.size}rem, ${3 * titleS.size}vw, ${4 * titleS.size}rem)`,
                  textShadow: titleS.textShadow,
                  marginBottom: 'clamp(0.5rem, 1vh, 1.5rem)',
                  transform: offsetTransform(titleS),
                }}
                data-testid="timer-circle-label"
              >
                {t('timer.circle')} {circleNumber}
              </div>
            );
          })()}
          {(() => {
            const roundS = ss(theme, "circle_run", "round_label", { color: roundColor });
            return (
              <div
                className="font-black uppercase tracking-widest leading-none"
                style={{
                  color: roundS.color,
                  fontSize: `clamp(${3 * roundS.size}rem, ${6 * roundS.size}vw, ${8 * roundS.size}rem)`,
                  textShadow: roundS.textShadow,
                  transform: offsetTransform(roundS),
                }}
              >
                {t('timer.round')} {circleSeg?.round || 1}
              </div>
            );
          })()}
          <TimerDisplay timer={timer} variant="huge" sizeMultiplier={timerSizePct} playSounds={soundEnabled} showProgress={false} screen="circle_run" />
        </div>
      </div>
    );
  }

  return null;
};

const SideAthlete = ({ athlete, active, side, theme, sizePct, t }) => {
  if (!athlete) return <div />;
  // Left side pushes towards the LEFT edge, right side pushes to the RIGHT
  // edge — so a single offset_x value in Settings produces a strictly
  // symmetric layout across the screen center.
  const mirrorX = side === "left";
  const highlightS = ss(theme, "battle_run", "active_highlight");
  const highlightColor = highlightS.color || "#facc15";
  const glowOp = highlightS.glow ?? 0.55;
  const highlightSize = highlightS.size ?? 1;
  const highlightOffset = active ? offsetTransformMirrored(highlightS, mirrorX) : "";
  const ringPx = Math.max(1, Math.round(6 * highlightSize));
  const glowPx = Math.round(60 * highlightSize);
  const ringColor = active ? highlightColor : "rgba(255,255,255,0.18)";
  const glow = active ? `0 0 ${glowPx}px ${highlightColor}${Math.round(glowOp * 255).toString(16).padStart(2, "0")}` : "none";
  const photoS = ss(theme, "battle_run", "side_photo");
  const nameS = ss(theme, "battle_run", "side_name");
  const countryS = ss(theme, "battle_run", "side_country");
  const wrS = ss(theme, "battle_run", "side_wr", { color: theme?.display_wr_color });
  const baseVw = 18;
  const photoScale = photoS.size || 1;
  const photoSize = `clamp(${140 * photoScale}px, ${(baseVw * sizePct * photoScale) / 100}vw, ${(360 * sizePct * photoScale) / 100}px)`;
  return (
    <div
      className="flex flex-col items-center"
      data-testid={`timer-side-athlete-${side}`}
      style={{ transform: highlightOffset || undefined, transition: "transform 200ms ease-out" }}
    >
      <div
        className="relative inline-block"
        style={{
          filter: active ? `drop-shadow(0 0 ${Math.round(25 * highlightSize)}px ${highlightColor}${Math.round(glowOp * 200).toString(16).padStart(2, "0")})` : "none",
          transform: offsetTransformMirrored(photoS, mirrorX),
        }}
      >
        {athlete.photo_url ? (
          <img
            src={getImg(athlete.photo_url)}
            alt={athlete.name}
            className="rounded-full object-cover transition-all duration-300"
            style={{ width: photoSize, height: photoSize, border: `${ringPx}px solid ${ringColor}`, boxShadow: glow }}
          />
        ) : (
          <div
            className="rounded-full flex items-center justify-center font-bold"
            style={{
              width: photoSize,
              height: photoSize,
              border: `${ringPx}px solid ${ringColor}`,
              background: photoS.bg_color || theme?.primary_color || "#ec4899",
              color: photoS.color || "#ffffff",
              fontSize: `clamp(2rem, ${(5 * sizePct) / 100}vw, 8rem)`,
              boxShadow: glow,
            }}
          >
            {athlete.name?.charAt(0) || "?"}
          </div>
        )}
        <FlagBadge countryName={athlete.country} theme={theme} screen="battle_run" element="side_flag" mirrorX={mirrorX} />
      </div>
      <div className="text-center mt-6" style={{ fontFamily: "Bebas Neue, sans-serif" }}>
        <div
          className="font-bold uppercase tracking-wider"
          style={{
            fontSize: `clamp(${1.5 * nameS.size}rem, ${(3.5 * sizePct * nameS.size) / 100}vw, ${(4.5 * sizePct * nameS.size) / 100}rem)`,
            color: active ? highlightColor : nameS.color,
            transform: offsetTransformMirrored(nameS, mirrorX),
            textShadow: nameS.textShadow,
          }}
        >
          {athlete.name}
        </div>
        <div
          className="mt-1"
          style={{
            fontSize: `clamp(${0.9 * countryS.size}rem, ${(1.6 * sizePct * countryS.size) / 100}vw, ${(2 * sizePct * countryS.size) / 100}rem)`,
            color: countryS.color,
            transform: offsetTransformMirrored(countryS, mirrorX),
            textShadow: countryS.textShadow,
          }}
        >
          {athlete.country}
        </div>
        {athlete.world_ranking && (
          <div
            className="font-bold mt-1"
            style={{
              color: wrS.color || "#fbbf24",
              fontSize: `clamp(${1 * wrS.size}rem, ${(2 * sizePct * wrS.size) / 100}vw, ${(2.5 * sizePct * wrS.size) / 100}rem)`,
              transform: offsetTransformMirrored(wrS, mirrorX),
              textShadow: wrS.textShadow,
            }}
          >
            #{athlete.world_ranking}
          </div>
        )}
      </div>
    </div>
  );
};

const CornerAthlete = ({ athlete, active, position, positionLabel, forfeited, empty, theme, sizePct, t }) => {
  // Bottom corners: lift them slightly higher (~10px+ as requested)
  const isBottom = position.startsWith("bottom-");
  // Each corner mirrors the offsets so a single offset_x/offset_y in the
  // Settings pushes ALL corners away from the screen center by the same
  // amount, keeping the 4-way layout strictly symmetric.
  const mirrorX = position.endsWith("-left");
  const mirrorY = position.startsWith("top-");
  const baseClasses = "absolute p-6 max-w-[28rem]";
  const offset = 24; // base spacing
  const bottomOffset = offset + 16; // ~16px higher than the original bottom edge
  const posStyle = {
    "top-left": { top: offset, left: offset },
    "top-right": { top: offset, right: offset, textAlign: 'right' },
    "bottom-right": { bottom: bottomOffset, right: offset, textAlign: 'right' },
    "bottom-left": { bottom: bottomOffset, left: offset },
  }[position] || {};

  const cardS = ss(theme, "circle_run", "corner_card_bg");
  const photoS = ss(theme, "circle_run", "corner_photo");
  const nameS = ss(theme, "circle_run", "corner_name");
  const countryS = ss(theme, "circle_run", "corner_country");
  const posS = ss(theme, "circle_run", "corner_position");
  const highlightS = ss(theme, "circle_run", "active_highlight");
  const highlightColor = highlightS.color || "#facc15";
  const glowOp = highlightS.glow ?? 0.55;
  const highlightSize = highlightS.size ?? 1;
  const highlightBorderPx = active ? Math.max(1, Math.round(4 * highlightSize)) : 2;
  const highlightGlowPx = Math.round(60 * highlightSize);
  const highlightScale = active ? 1 + 0.05 * highlightSize : 1;
  const _hlX = (highlightS.offset_x || 0) * (mirrorX ? -1 : 1);
  const _hlY = (highlightS.offset_y || 0) * (mirrorY ? -1 : 1);
  const highlightOffset = active ? ` translate(${_hlX}px, ${_hlY}px)` : "";

  const cardStyle = {
    background: cardS.bg_color || "rgba(0,0,0,0.65)",
    boxShadow: active ? `0 0 ${highlightGlowPx}px ${highlightColor}${Math.round(glowOp * 255).toString(16).padStart(2, "0")}` : undefined,
    border: `${active ? highlightBorderPx : 2}px solid ${active ? highlightColor : (cardS.border_color || "rgba(255,255,255,0.15)")}`,
    transform: `scale(${highlightScale}) ${offsetTransformMirrored(cardS, mirrorX, mirrorY)}${highlightOffset}`.trim(),
    // Anchor the card to its outer corner so any scale/size change grows
    // INWARD (toward the canvas center) and never past the corner edge.
    transformOrigin: `${position.split("-")[0]} ${position.split("-")[1]}`,
    transition: "transform 200ms ease-out, box-shadow 200ms ease-out",
    // Prevent inner content overflow if the user shrinks the card below the
    // natural photo+text size.
    overflow: "hidden",
    ...(cardS.width && cardS.width !== 1 ? { width: `${cardS.width * 100}%` } : {}),
    ...(cardS.height && cardS.height !== 1 ? { minHeight: `${cardS.height * 100}%` } : {}),
  };

  const photoPx = Math.round(96 * (sizePct / 100) * (photoS.size || 1));

  if (empty) {
    return (
      <div className={baseClasses} style={{ ...posStyle, opacity: 0.3, transform: `scale(${sizePct/100})`, transformOrigin: `${position.split('-')[0]} ${position.split('-')[1]}` }}>
        <div className="rounded-2xl bg-black/40 ring-2 ring-white/15 p-4 backdrop-blur-md">
          <div className="text-2xl text-white/40">{positionLabel || "—"}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${baseClasses} transition-all duration-300`} style={{ ...posStyle, transform: `scale(${sizePct/100})`, transformOrigin: `${position.split('-')[0]} ${position.split('-')[1]}` }}>
      <div className="rounded-2xl p-4 backdrop-blur-md" style={cardStyle}>
        <div className="flex items-center gap-3">
          <div className="relative inline-block">
            {athlete?.photo_url ? (
              <img
                src={getImg(athlete.photo_url)}
                alt={athlete.name}
                className="rounded-full object-cover"
                style={{ width: `${photoPx}px`, height: `${photoPx}px`, border: `2px solid ${photoS.border_color || "rgba(255,255,255,0.3)"}` }}
              />
            ) : (
              <div
                className="rounded-full flex items-center justify-center font-bold"
                style={{
                  width: `${photoPx}px`,
                  height: `${photoPx}px`,
                  background: photoS.bg_color || theme?.primary_color || "#ec4899",
                  color: photoS.color || "#ffffff",
                  fontSize: `${photoPx * 0.36}px`,
                }}
              >
                {athlete?.name?.charAt(0) || "?"}
              </div>
            )}
            <FlagBadge countryName={athlete?.country} theme={theme} screen="circle_run" element="corner_flag" mirrorX={mirrorX} />
          </div>
          <div className={position.includes("right") ? "text-right" : ""}>
            {positionLabel && (
              <div
                className="font-bold"
                style={{
                  color: posS.color,
                  fontSize: `${0.85 * (posS.size || 1)}rem`,
                  transform: offsetTransformMirrored(posS, mirrorX, mirrorY),
                  textShadow: posS.textShadow,
                }}
              >
                {positionLabel}
              </div>
            )}
            <div
              className={`font-bold ${forfeited ? "line-through" : ""}`}
              style={{
                color: forfeited ? "#f87171" : (active ? highlightColor : nameS.color),
                fontSize: `${1.75 * (nameS.size || 1)}rem`,
                transform: offsetTransformMirrored(nameS, mirrorX, mirrorY),
                textShadow: nameS.textShadow,
              }}
            >
              {athlete?.name || "—"}
            </div>
            <div
              style={{
                color: countryS.color,
                fontSize: `${0.95 * (countryS.size || 1)}rem`,
                transform: offsetTransformMirrored(countryS, mirrorX, mirrorY),
                textShadow: countryS.textShadow,
              }}
            >
              {athlete?.country}
            </div>
            {forfeited && <div className="text-sm text-red-400 font-bold uppercase">{t('timer.forfeit')}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimerActiveDisplay;
